import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  FlaskConical,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import {
  createTestCase,
  getTestCase,
  TEST_ACTORS,
  TEST_MODULES,
  TestCase,
  TestCasePriority,
  TestCaseStatus,
  TestStep,
  updateTestCase,
} from '../services/testCasesApi';

const DEFAULT_TC: Partial<TestCase> = {
  moduleId: '',
  externalRef: '',
  name: '',
  description: '',
  priority: 'MEDIUM',
  status: 'DRAFT',
  primaryActor: '',
  secondaryActors: [],
  preconditions: [],
  postconditions: [],
  steps: [],
  testData: [],
  relatedTestCases: [],
  tags: [],
};

export function TestCaseFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();

  const [form, setForm] = useState<Partial<TestCase>>(DEFAULT_TC);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Temporary text buffers for list fields
  const [precondInput, setPrecondInput] = useState('');
  const [postcondInput, setPostcondInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!isEdit || !id) return;
    void (async () => {
      try {
        const tc = await getTestCase(id);
        setForm(tc);
      } catch (err: any) {
        showToast(err.message || 'Erreur lors du chargement', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const set = <K extends keyof TestCase>(key: K, value: TestCase[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Steps helpers
  const addStep = () => {
    const steps = [...(form.steps ?? [])];
    steps.push({ order: steps.length + 1, action: '', expectedResult: '' });
    set('steps', steps);
  };

  const updateStep = (idx: number, field: keyof TestStep, value: string) => {
    const steps = [...(form.steps ?? [])].map((s, i) =>
      i === idx ? { ...s, [field]: value } : s,
    );
    set('steps', steps);
  };

  const removeStep = (idx: number) => {
    const steps = (form.steps ?? []).filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 }));
    set('steps', steps);
  };

  // List field helpers
  const addToList = (field: 'preconditions' | 'postconditions' | 'tags', value: string) => {
    if (!value.trim()) return;
    const list = [...(form[field] as string[] ?? [])];
    if (!list.includes(value.trim())) list.push(value.trim());
    set(field, list as any);
  };

  const removeFromList = (field: 'preconditions' | 'postconditions' | 'tags', idx: number) => {
    const list = [...(form[field] as string[] ?? [])].filter((_, i) => i !== idx);
    set(field, list as any);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.moduleId || !form.name || !form.primaryActor) {
      showToast('Module, nom et acteur principal sont obligatoires.', 'error');
      return;
    }
    try {
      setSaving(true);
      if (isEdit && id) {
        await updateTestCase(id, form);
        showToast('Cas de test mis à jour', 'success');
      } else {
        const tc = await createTestCase(form);
        showToast('Cas de test créé', 'success');
        navigate(`/test-cases/${tc.id}`);
        return;
      }
      navigate(`/test-cases/${id}`);
    } catch (err: any) {
      showToast(err.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ToastComponent />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/test-cases')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-primary-600" />
            {isEdit ? 'Modifier le cas de test' : 'Nouveau cas de test'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate('/test-cases')} className="btn-secondary">
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? 'Enregistrer les modifications' : 'Créer le cas de test'}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* ── Top row: identity (left) + conditions (right) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

          {/* Identity — takes 2/3 of the width on xl */}
          <div className="xl:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">Identification</h2>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Module <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={form.moduleId ?? ''}
                    onChange={(e) => set('moduleId', e.target.value)}
                    required
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Sélectionner un module</option>
                    {TEST_MODULES.map((m) => <option key={m.id} value={m.id}>{m.label} ({m.id})</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Référence UC (EA)</label>
                <input
                  type="text"
                  value={form.externalRef ?? ''}
                  onChange={(e) => set('externalRef', e.target.value)}
                  placeholder="ex: UC-001"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name ?? ''}
                onChange={(e) => set('name', e.target.value)}
                required
                placeholder="ex: Créer un compte client — flux nominal adulte"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
                rows={4}
                placeholder="Décrivez l'objectif de ce cas de test..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Acteur principal <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={form.primaryActor ?? ''}
                    onChange={(e) => set('primaryActor', e.target.value)}
                    required
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Sélectionner un acteur</option>
                    {TEST_ACTORS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                <div className="relative">
                  <select
                    value={form.priority ?? 'MEDIUM'}
                    onChange={(e) => set('priority', e.target.value as TestCasePriority)}
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="CRITICAL">Critique</option>
                    <option value="HIGH">Haute</option>
                    <option value="MEDIUM">Moyenne</option>
                    <option value="LOW">Basse</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <div className="relative">
                  <select
                    value={form.status ?? 'DRAFT'}
                    onChange={(e) => set('status', e.target.value as TestCaseStatus)}
                    className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-lg text-sm appearance-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="DRAFT">Brouillon</option>
                    <option value="ACTIVE">Actif</option>
                    <option value="DEPRECATED">Déprécié</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(form.tags ?? []).map((tag, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 text-xs px-2 py-1 rounded-full">
                    {tag}
                    <button type="button" onClick={() => removeFromList('tags', i)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addToList('tags', tagInput); setTagInput(''); }
                  }}
                  placeholder="Ajouter un tag et appuyer Entrée"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                />
                <button type="button" onClick={() => { addToList('tags', tagInput); setTagInput(''); }} className="btn-secondary text-sm px-3">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Pre/Post conditions — takes 1/3 on xl */}
          <div className="xl:col-span-1 bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
            <h2 className="text-base font-semibold text-gray-900">Conditions</h2>

            {(['preconditions', 'postconditions'] as const).map((field) => {
              const isPre = field === 'preconditions';
              const inputVal = isPre ? precondInput : postcondInput;
              const setInput = isPre ? setPrecondInput : setPostcondInput;
              return (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {isPre ? '✅ Pré-conditions' : '🏁 Post-conditions'}
                  </label>
                  <ul className="space-y-1.5 mb-2">
                    {((form[field] as string[]) ?? []).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2 text-sm">
                        <span className="text-gray-400 font-mono text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                        <span className="flex-1 text-gray-700 leading-snug">{item}</span>
                        <button type="button" onClick={() => removeFromList(field, i)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inputVal}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addToList(field, inputVal); setInput(''); }
                      }}
                      placeholder={isPre ? "Ajouter une pré-condition…" : "Ajouter une post-condition…"}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                    />
                    <button type="button" onClick={() => { addToList(field, inputVal); setInput(''); }} className="btn-secondary text-sm px-3">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Steps — full width ── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4 mt-6">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">Étapes du test</h2>
            <button type="button" onClick={addStep} className="btn-secondary inline-flex items-center gap-2 text-sm">
              <Plus className="w-4 h-4" />
              Ajouter une étape
            </button>
          </div>

          {(form.steps ?? []).length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm border-2 border-dashed rounded-lg">
              Aucune étape définie. Cliquez sur "Ajouter une étape".
            </div>
          ) : (
            <div className="space-y-3">
              {(form.steps ?? []).map((step, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-6 h-6 flex items-center justify-center bg-primary-100 text-primary-700 text-xs font-bold rounded-full flex-shrink-0">
                      {step.order}
                    </span>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Étape {step.order}</span>
                    <button type="button" onClick={() => removeStep(idx)} className="ml-auto text-gray-400 hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Action / Navigation</label>
                      <textarea
                        value={step.action}
                        onChange={(e) => updateStep(idx, 'action', e.target.value)}
                        rows={3}
                        placeholder="ex: Cliquer sur le bouton 'S'inscrire' dans le header"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Résultat attendu</label>
                      <textarea
                        value={step.expectedResult}
                        onChange={(e) => updateStep(idx, 'expectedResult', e.target.value)}
                        rows={3}
                        placeholder="ex: La page /register s'affiche avec le formulaire en étape 1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500 resize-none"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Bottom action bar ── */}
        <div className="flex items-center justify-between gap-4 pt-4 pb-6">
          <button type="button" onClick={() => navigate('/test-cases')} className="btn-secondary">
            Annuler
          </button>
          <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isEdit ? 'Enregistrer les modifications' : 'Créer le cas de test'}
          </button>
        </div>
      </form>
    </div>
  );
}
